import {
  Component,
  OnInit,
  OnDestroy,
  EventEmitter,
  Output,
  Input
} from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { map, filter, take, catchError } from 'rxjs/operators';
import { SubSink } from 'subsink';
import { Observable, of } from 'rxjs';
import { Title } from "@angular/platform-browser";

import {
  CoreService,
  AuthService,
  AuthActionTypes,
  Login,
  ForgotPasswordRedirect,
  LoginByToken,
  ExternalUserLogin,
  ExternalUserLoginByToken,
  GeneralActionTypes,
  UserNotificationAction,
  AuthStore,
  BrowserCookieService
} from '@arc/arc-shared-frontend';
import { RefreshService } from '../services/refresh.service';
import { RoutingService } from '../../navigation/services/routing.service';

@Component({
  selector: 'app-authentication-login-container',
  templateUrl: './login.container.component.html',
  styleUrls: ['./login.container.component.scss']
})
export class LoginContainerComponent implements OnInit, OnDestroy {
  constructor(
    private store: Store<any>,
    public coreService: CoreService,
    private route: ActivatedRoute,
    private actions$: Actions,
    private authService: AuthService,
    private routingService: RoutingService,
    private cookieService: BrowserCookieService,
    private refreshService: RefreshService
  ) {}

  @Input() widgetMode: 'regular' | 'external' = 'regular';
  @Input() applicationId = '';
  @Input() featureId = '';
  @Input() personaId = '';
  @Input() redirectUrl = '';
  @Input() tenantId = '';
  @Input() newUserWelcomeTemplateTypeId = '';
  @Input() existingUserWelcomeTemplateTypeId = '';
  @Input() fromExternal;

  @Output()
  loginSucceeded = new EventEmitter<any>();
  @Output()
  loginError = new EventEmitter<any>();

  @Output()
  loginButtonClicked = new EventEmitter<any>();

  @Output()
  forgetPasswordButtonClicked = new EventEmitter<any>();

  submitting$: Observable<boolean>;

  msgs = [];
  errmsg = '';
  showresetpassword = false;
  private subs = new SubSink();
  _returnUrl = '';
  _logoutUrl = '';
  _tenantId = '';
  _oidc = false;

  _isBypass: boolean = true;

  isClientSessionCloseWindow: boolean = false;

  public get isBypass() {
    return this._isBypass;
  };

  public set isBypass(value: boolean){
    this._isBypass = value;
    this.refreshService.isBypass$.next(this._isBypass);
  }

  ngOnInit() {
    const isClientSession =
      (this.route.snapshot.queryParams['dualsession'] || '') === '1';
    if (isClientSession) {
      this.coreService.setClientConnectLocalStorage('1');
    } else {
      this.coreService.clearClientConnectLocalStorage();
    }

    this.isClientSessionCloseWindow = this.coreService.getClientConnectFromLocalStorage() === '1';
    
    this.refreshService.isBypass$.next(this._isBypass);
    this.route.queryParams.subscribe(params => {
      let token = params['token'];
      this._tenantId = params['tenantId'];
      this._returnUrl = params['returnUrl'];
      this._logoutUrl = params['logoutUrl'] || params['logoutRedirectUrl'];
      this._oidc = params['oidc'] === 'oidc';
      this.submitting$ = this.store
        .select(AuthStore)
        .pipe(select(x => x.user.submitting));
      if (token) {
        const accessToken = params['token'];

        if (accessToken && this.widgetMode !== 'external') {
          this.store.dispatch(
            new LoginByToken({
              accessToken: accessToken,
              tenantId: this._tenantId,
              returnUrl: this._returnUrl,
              logoutUrl: this._logoutUrl,
              isIdentityProvider: this._oidc
            })
          );
        } else if (accessToken && this.widgetMode === 'external') {
          const loginRequest = {
            accessToken: accessToken,
            tenantId: this._tenantId,
            returnUrl: this._returnUrl,
            logoutUrl: this._logoutUrl,
            isIdentityProvider: this._oidc
          };
          this.store.dispatch(new ExternalUserLoginByToken(loginRequest));
        } else {
          this.isBypass = false;
        }
      } else {
        token = this.coreService.getAuthTokenFromLocalStorage();
        const activeTenantString =
          this.coreService.getActiveTenantFromLocalStorage();
        const activeTenant = activeTenantString
          ? JSON.parse(activeTenantString)
          : {};
        const tenantId = activeTenant ? activeTenant.tenantId : null;
        this.verifyTokenIsValid$(token).subscribe(verified => {
          if (token && verified && this.widgetMode !== 'external') {
            const returnUrl: string = `${this.route.snapshot.queryParams.returnUrl}`;
            let loginActionParam: LoginByToken =  new LoginByToken({
              accessToken: token,
              tenantId: tenantId,
              silentError: true,
              isIdentityProvider: this._oidc,
            })

            if (returnUrl !== 'undefined' ) {
              loginActionParam.payload.returnUrl = returnUrl;
            }
            this.store.dispatch(
             loginActionParam
            );
          } else if (token && this.widgetMode === 'external' && verified) {
            const loginRequest = {
              accessToken: token,
              tenantId: this._tenantId,
              returnUrl: this._returnUrl,
              logoutUrl: this._logoutUrl,
              isIdentityProvider: this._oidc
            };
            this.store.dispatch(new ExternalUserLoginByToken(loginRequest));
          } else {
            this.isBypass = false;
          }
        });
      }
    });

    this.subs.add(
      this.actions$
        .pipe(
          ofType(GeneralActionTypes.UserNotification),
          map((action: UserNotificationAction) => action.payload),
          filter(x => x.type === 'danger')
        )
        .subscribe(x => {
          this.isBypass = false;
        })
    );
    this.subs.add(
      this.actions$.pipe(ofType(GeneralActionTypes.DoNothing)).subscribe(x => {
        this.isBypass = false;
      })
    );

    this.subs.add(
      this.actions$.pipe(ofType(AuthActionTypes.LoginSuccess)).subscribe(x => {
        this.loginSucceeded.emit();
      })
    );
    this.subs.add(
      this.actions$.pipe(ofType(AuthActionTypes.LoginFailure)).subscribe(x => {
        this.loginError.emit();
      })
    );
  }

  verifyTokenIsValid$(token): Observable<boolean> {
    if (!token) {
      return of(false) as Observable<boolean>;
    }
    return this.authService.V1VerifysessionPost({ accessToken: token }).pipe(
      map(x => x.body),
      map(x => x.verified),
      catchError(error => {
        this.cookieService.removeCookie('token');
        return of(false) as Observable<boolean>;
      })
    );
  }

  onSignin(user: any) {
    this.loginButtonClicked.emit();
    this.msgs = [];
    if (this.widgetMode === 'external') {
      this.store.dispatch(
        new ExternalUserLogin({
          ...user,
          returnUrl: this._returnUrl,
          tenantId: this.tenantId,
          applicationId: this.applicationId,
          featureId: this.featureId,
          personaId: this.personaId,
          redirectUrl: this.redirectUrl,
          newUserWelcomeTemplateTypeId: this.newUserWelcomeTemplateTypeId,
          existingUserWelcomeTemplateTypeId:
            this.existingUserWelcomeTemplateTypeId
        })
      );
    } else {
      let initialRoute = this.routingService.retrieveRoute() || 'invoicing';
      if (initialRoute === this.routingService.collectionsWorkspaceV2URL) {
        location.href = initialRoute;
      }

      initialRoute = this.validLastRoute(initialRoute);

      this.store.dispatch(
        new Login({
          ...user,
          returnUrl: this._returnUrl || initialRoute,
          tenantId: this._tenantId,
          isIdentityProvider: this._oidc
        })
      );
    }
  }

  validLastRoute = (route: string | null | undefined): string => {
    switch(route) {
      case 'invoicing':
      case 'bpn':
      case 'payments':
      case 'cash-app':
      case 'collections':
      case this.routingService.collectionsWorkspaceV2URL:
      case 'bt-collections':
      case 'credit-manager':
      case 'disputes':
        return route;
      case 'undefined':
      default:
        return 'invoicing';
    }
  };

  onForgetPassword(email: string) {
    this.forgetPasswordButtonClicked.emit();
    this.store.dispatch(new ForgotPasswordRedirect(email));
  }

  public ngOnDestroy() {
    this.subs.unsubscribe();
  }
}
